import React, { useState } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, Space, message, Statistic, Row, Col } from 'antd';
import { LinkOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useListUrlMonitorsQuery, useAddUrlMonitorMutation, useDeleteUrlMonitorMutation,
  useCheckAllUrlsMutation, useCheckSingleUrlMutation, useListProjectsQuery
} from '../store/api';

const { Option } = Select;

const MonitorUrl = () => {
  const { data = [], isLoading, refetch } = useListUrlMonitorsQuery();
  const { data: projects = [] } = useListProjectsQuery();
  const [addMonitor] = useAddUrlMonitorMutation();
  const [deleteMonitor] = useDeleteUrlMonitorMutation();
  const [checkAll, { isLoading: checking }] = useCheckAllUrlsMutation();
  const [checkOne] = useCheckSingleUrlMutation();
  const [modal, setModal] = useState(false);
  const [form] = Form.useForm();

  const up = data.filter(m => m.ultimo_estado === 'up').length;
  const down = data.filter(m => m.ultimo_estado === 'down').length;

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      await addMonitor(values).unwrap();
      message.success('Monitor creado'); setModal(false); form.resetFields(); refetch();
    } catch { message.error('Error al crear monitor'); }
  };

  const handleCheckAll = async () => {
    try {
      await checkAll().unwrap();
      message.success('Verificación completada'); refetch();
    } catch { message.error('Error al verificar'); }
  };

  const columns = [
    { title: 'Nombre', dataIndex: 'nombre', render: (t, r) => t || r.url?.substring(0, 40) },
    { title: 'URL', dataIndex: 'url', render: u => <a href={u} target="_blank" rel="noreferrer">{u?.substring(0, 60)}</a> },
    { title: 'Proyecto', dataIndex: 'proyecto_id', render: id => projects.find(p => p.id === id)?.nombre || '-' },
    { title: 'Estado', dataIndex: 'ultimo_estado', render: s => (
      <Tag color={s === 'up' ? 'green' : s === 'down' ? 'red' : 'default'} icon={s === 'up' ? <CheckCircleOutlined /> : s === 'down' ? <CloseCircleOutlined /> : <ClockCircleOutlined />}>
        {s?.toUpperCase() || 'UNKNOWN'}
      </Tag>
    )},
    { title: 'Tiempo (ms)', dataIndex: 'ultimo_tiempo_ms', render: t => t ? `${t}ms` : '-' },
    { title: 'Uptime 24h', dataIndex: 'uptime_24h', render: u => u ? <span style={{ color: parseFloat(u) >= 99 ? '#52c41a' : parseFloat(u) >= 95 ? '#faad14' : '#f5222d' }}>{u}%</span> : '-' },
    { title: 'Último check', dataIndex: 'ultimo_check', render: d => d ? dayjs(d).format('DD/MM HH:mm') : '-' },
    { title: '', key: 'act', width: 80, render: (_, r) => (
      <Space size={4}>
        <Button size="small" icon={<ReloadOutlined />} onClick={async () => { await checkOne(r.id).unwrap(); message.success('Verificado'); refetch(); }} />
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({
          title: '¿Eliminar?', onOk: async () => { await deleteMonitor(r.id).unwrap(); refetch(); }
        })} />
      </Space>
    )},
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><LinkOutlined style={{ marginRight: 8 }} />Monitor URLs</h1>
          <div className="page-subtitle">{data.length} URLs monitoreadas</div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleCheckAll} loading={checking}>Verificar Todas</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModal(true); }}>Agregar URL</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title="Activas" value={up} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Caídas" value={down} valueStyle={{ color: '#f5222d' }} prefix={<CloseCircleOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Total" value={data.length} prefix={<LinkOutlined />} /></Card></Col>
      </Row>

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Table columns={columns} dataSource={data} rowKey="id" loading={isLoading} pagination={{ pageSize: 15 }} size="middle" scroll={{ x: true }} />
      </Card>

      <Modal title="Agregar Monitor URL" open={modal} onOk={handleAdd} onCancel={() => setModal(false)} okText="Crear">
        <Form form={form} layout="vertical">
          <Form.Item name="nombre" label="Nombre"><Input /></Form.Item>
          <Form.Item name="url" label="URL" rules={[{ required: true }]}><Input placeholder="https://..." /></Form.Item>
          <Form.Item name="proyecto_id" label="Proyecto">
            <Select allowClear placeholder="Opcional">{projects.map(p => <Option key={p.id} value={p.id}>{p.nombre}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="metodo" label="Método" initialValue="GET">
            <Select>{['GET','HEAD','POST'].map(m => <Option key={m} value={m}>{m}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="esperado_http" label="HTTP esperado" initialValue={200}><Input type="number" /></Form.Item>
          <Form.Item name="intervalo_seg" label="Intervalo (seg)" initialValue={300}><Input type="number" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MonitorUrl;
